import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { pathOr, path } from 'ramda';
import { parse } from 'query-string';
import axios from 'axios';

// Components
import {
  DataBlocks,
  Button,
  Icon,
  I18n,
  Tooltip,
} from 'components';

// Utils/Analytics
import printAccumulator, { getProgressBarColor } from 'utils/accumulators';
import {
  formatPhone,
  formatDateString,
  formatUnitString,
  templateParser,
} from 'utils/formatString';
import declint from 'utils/declint';

import classNames from 'classnames/bind';
import styles from './styles.pcss';

import SharingDiscount from '../../components/SharingDiscount';

const cx = classNames.bind(styles);

const formattedPhone = (str) => formatPhone(str).replace(/-/g, ' ');

class BundlesSharing extends Component {
  scrollToBlock = parse(window.location.search).scrollTo === 'seb-connect-block';

  constructor(props) {
    super(props);

    this.state = {
      waiting: false,
    };
  }

  componentDidMount() {
    if (this.scrollToBlock) {
      window.history
        .replaceState(null, null, window.location.href.replace('scrollTo=seb-connect-block', ''));
    }
  }

  handleClick = () => {
    this.setState({ waiting: true });
    const formattedCtn = formattedPhone(this.props.ctn);

    new Promise((resolve, reject) => {
      if (!this.props.data.upsaleUrl) {
        return resolve();
      }

      axios.get(this.props.data.upsaleUrl)
        .then(({ data }) => {
          if (data) reject(data);

          resolve();
        })
        .catch(resolve);
    })
      .then(() => {
        const { invites, limit } = this.props.data;

        if (invites.length < limit) {
          this.props.actions.addNumber(formattedCtn);
        } else {
          this.props.actions.showFamilyExpansionError();
        }
      })
      .catch((data) => {
        this.props.actions.fetchUpsalePopupContent(data);
        this.props.actions.openUpsalePopup();
      })
      .finally(() => this.setState({ waiting: false }));
  };

  render() {
    if (!this.props.data.isAvailable) return null;

    const {
      unit,
      price,
      isSeb,
      invites,
      texts,
      accInternet,
      progressBarLimits,
      catalogUrl,
      discountParams,
      familyDiscountParams,
      connectedLeftNumberText,
      limit,
    } = this.props.data;

    const { switchNumber, disconnectNumber } = this.props.actions;
    const { isRestrictedAccess } = this.props;

    const accumulatorDescription = templateParser(texts.sebAccumulatorDescription, { catalogUrl });
    const emptyProfileMessage = templateParser(texts.emptyProfileMessage, {
      sharedInternet: accInternet ? formatUnitString(accInternet.size, 'KBYTE') : '',
    });
    const addNumberCount = declint(limit, ['номер', 'номера', 'номеров']);

    // строчка "добавить ещё n номер/а/ов"
    const connectedLeftNumber = connectedLeftNumberText
      && `${connectedLeftNumberText.replace(/{{COUNT}}/g, limit)} ${addNumberCount}`;

    return (
      <DataBlocks.Block
        title={(
          <>
            <span dangerouslySetInnerHTML={{ __html: texts.profileSharingTitleMessage }} />
            &nbsp;
            <Icon className={cx('sharingIcon')} name="sharing" />
          </>
        )}
        nameId="seb-connect-block"
        scrollToBlock={this.scrollToBlock}
      >

        <div className={cx('sharingDiscount')}>
          <SharingDiscount
            discountParams={discountParams}
            familyDiscountParams={familyDiscountParams}
            price={price}
            invites={invites}
            texts={texts}
            isSeb={isSeb}
            unit={unit}
          />
        </div>

        {invites.length !== 0 ? (
          <>
            {accInternet && (
              <DataBlocks.Table className={cx('sharedServiceTable')}>
                <DataBlocks.TableRow>
                  <DataBlocks.TableCell className={cx('sharedServiceCell')}>
                    <div className={cx('sharedServiceTitle')}>
                      <span className={cx('iconCircle')}>
                        <img className={cx('iconImage')} src={accInternet.icon} alt="" />
                      </span>
                      <div>
                        {accInternet.title}
                      </div>
                      <Tooltip>
                        <span dangerouslySetInnerHTML={{ __html: accInternet.description }} />
                      </Tooltip>
                    </div>
                  </DataBlocks.TableCell>
                  <DataBlocks.TableCell className={cx('sharedServiceCell')}>
                    {accInternet.isUnlimited ? (
                      <I18n path="home.accumulators.unlimited" />
                    ) : (
                      <>
                        <div className={cx('accumulatorValue')}>
                          <I18n
                            path="home.accumulators.accumulatorInfo"
                            vars={{ info: printAccumulator(accInternet) }}
                          />
                        </div>
                        {accInternet.size >= accInternet.rest && progressBarLimits && (
                          <div className={cx('accumulatorProgress')}>
                            <div className={cx('progressBar')}>
                              <div
                                className={cx('progressBarValue')}
                                style={{ width: `${accInternet.rest / accInternet.size * 100}%` }}
                              >
                                <div
                                  className={cx(
                                    'progressBarFill',
                                    getProgressBarColor(accInternet, progressBarLimits),
                                  )}
                                />
                              </div>
                            </div>
                            {accInternet.packages && accInternet.packages.length && (
                              <Tooltip width={240}>
                                <div key={accInternet.packages[0].title}>
                                  <div className={cx('packageSize')}>
                                    {formatUnitString(accInternet.packages[0].size, accInternet.unit)}
                                  </div>
                                  <div
                                    className={cx('packageName')}
                                    dangerouslySetInnerHTML={{
                                      __html: accInternet.packages[0].title,
                                    }}
                                  />
                                </div>
                              </Tooltip>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </DataBlocks.TableCell>

                  {accInternet.unit === 'KBYTE' && accInternet.resetDate && (
                    <DataBlocks.TableCell className={cx('sharedServiceCell')}>
                      <div className={cx('updateInfo')}>
                        <I18n
                          path="home.accumulators.updateDate"
                          vars={{
                            date: formatDateString(accInternet.resetDate, 'DD.MM.YY'),
                          }}
                        />
                      </div>
                    </DataBlocks.TableCell>
                  )}
                </DataBlocks.TableRow>
                <DataBlocks.TableRow>
                  <DataBlocks.TableCell colSpan="3">
                    <div
                      className={cx('otherPackagesText')}
                      dangerouslySetInnerHTML={{ __html: accumulatorDescription }}
                    />
                  </DataBlocks.TableCell>
                </DataBlocks.TableRow>
              </DataBlocks.Table>
            )}

            <DataBlocks.Table className={cx('sharedNumbers', { withoutAccumulator: !accInternet })}>
              <DataBlocks.TableRow>
                <DataBlocks.TableCell colSpan="4" className={cx('sharedNumbersTitleCell')}>
                  <div className={cx('sharedNumbersTitle')}>
                    Подключённые к тарифу номера
                  </div>
                </DataBlocks.TableCell>
              </DataBlocks.TableRow>
              {invites.map((invite, key) => (
                <DataBlocks.TableRow
                  className={cx('blockTableRow')}
                  key={`invite-ctn-${key}`}
                >
                  <DataBlocks.TableCell>
                    {invite.ctn ? formattedPhone(invite.ctn) : `Номер ${key + 1}`}
                  </DataBlocks.TableCell>
                  <DataBlocks.TableCell>
                    {invite.number}
                  </DataBlocks.TableCell>
                  <DataBlocks.TableCell
                    className={cx('blockTableCell', {
                      successCell: invite.status.toLowerCase() === 'confirmed',
                    })}
                  >
                    {invite.status.toLowerCase() === 'confirmed' ? (
                      <I18n path="home.bundlesSharing.statusConfirmed" />
                    ) : (
                      <I18n path="home.bundlesSharing.statusNotConfirmed" />
                    )}
                  </DataBlocks.TableCell>
                  <DataBlocks.TableCell className={cx('iconsCell')}>
                    {!isRestrictedAccess && (
                      <>
                        {invite.status.toLowerCase() === 'confirmed' && (
                          <button
                            className={cx('iconButton')}
                            onClick={() => switchNumber(formatPhone(invite.number || invite.ctn), invite.status)}
                          >
                            Изменить
                            <Icon className={cx('editIcon')} name="edit" />
                          </button>
                        )}
                        <button
                          className={cx('iconButton')}
                          onClick={() => disconnectNumber(formatPhone(invite.number || invite.ctn), invite.status)}
                        >
                          Удалить
                          <Icon className={cx('crossIcon')} name="cross" />
                        </button>
                      </>
                    )}
                  </DataBlocks.TableCell>
                </DataBlocks.TableRow>
              ))}

              <DataBlocks.TableRow>
                <DataBlocks.TableCell colSpan="4">
                  {isRestrictedAccess ? (
                    <span
                      className={cx('maxConnectedMessage')}
                      dangerouslySetInnerHTML={{ __html: texts.connectedMaxNumbersText }}
                    />
                  ) : (
                    <Button
                      onClick={this.handleClick}
                      isLoading={this.state.waiting}
                    >
                      {texts.settingButtonTitle}
                    </Button>
                  )}
                </DataBlocks.TableCell>
              </DataBlocks.TableRow>
            </DataBlocks.Table>
          </>
        ) : (
          <DataBlocks.Table>
            {!discountParams && (
              <DataBlocks.TableRow>
                <DataBlocks.TableCell>
                  <div dangerouslySetInnerHTML={{ __html: emptyProfileMessage }} />
                </DataBlocks.TableCell>
              </DataBlocks.TableRow>
            )}
            <DataBlocks.TableRow>
              <DataBlocks.TableCell>
                {!isRestrictedAccess && (
                  <Button
                    onClick={this.handleClick}
                    isLoading={this.state.waiting}
                  >
                    <I18n path="home.bundlesSharing.connectButton" />
                  </Button>
                )}
                {connectedLeftNumber && (
                  <span className={cx('leftConnectedMessage')}>
                    {connectedLeftNumber}
                  </span>
                )}
              </DataBlocks.TableCell>
            </DataBlocks.TableRow>
          </DataBlocks.Table>
        )}
      </DataBlocks.Block>
    );
  }
}

BundlesSharing.propTypes = {
  data: PropTypes.shape({
    unit: PropTypes.string,
    isSeb: PropTypes.bool,
    discountParams: PropTypes.object,
    familyDiscountParams: PropTypes.object,
    isAvailable: PropTypes.bool,
    limit: PropTypes.number,
    priceWithoutDiscount: PropTypes.number,
    invites: PropTypes.array,
    connectedLeftNumberText: PropTypes.string,
    texts: PropTypes.shape({
      settingButtonTitle: PropTypes.string,
      connectedMaxNumbersText: PropTypes.string,
      emptyProfileMessage: PropTypes.string,
    }),
    accInternet: PropTypes.shape({ // optionally
      unit: PropTypes.string,
      title: PropTypes.string,
      description: PropTypes.string,
      rest: PropTypes.number,
      size: PropTypes.number,
      resetDate: PropTypes.string,
      icon: PropTypes.string,
      isUnlimited: PropTypes.bool,
      packages: PropTypes.arrayOf(PropTypes.shape({
        title: PropTypes.string,
        size: PropTypes.number,
        resetDate: PropTypes.string,
      })),
    }),
    progressBarLimits: PropTypes.objectOf(PropTypes.shape({
      min: PropTypes.number,
      max: PropTypes.number,
    })),
    catalogUrl: PropTypes.string,
    upsaleUrl: PropTypes.string,
  }),
  actions: PropTypes.object,
  isRestrictedAccess: PropTypes.bool,
  ctn: PropTypes.string,
};

const mapStateToProps = (state) => {
  const seb = pathOr(null, ['external', 'tariff', 'data', 'sharing'], state);
  const { catalogUrl } = pathOr({}, ['external', 'tariff', 'data'], state);
  const progressBarLimits = pathOr(null, ['progressBarLimits'], seb);

  if (!seb || !seb.info || !seb.texts || !seb.links) {
    return {
      data: { isAvailable: false },
    };
  }

  let data = {
    catalogUrl,
    texts: seb.texts,
    connectedLeftNumberText: seb.texts.discountParams?.connectedLeftNumberText,
    upsaleUrl: seb.links.upsaleUrl,
    isAvailable: seb.info.count > 0,
    isSeb: seb.info.isSeb,
    invites: seb.info.invites,
    limit: seb.info.count,
    accInternet: seb.info.internetAccumulator,
    unit: seb?.info?.unit,
    price: pathOr({}, ['info', 'price'], seb),
    discountParams: seb?.info?.discount ?? {},
    familyDiscountParams: seb?.info?.familyDiscountServices ?? {},
  };

  if (progressBarLimits) {
    data = {
      ...data,
      progressBarLimits,
    };
  }

  return { data };
};

const mapDispatchToProps = (dispatch) => {
  const actions = path(['beeline', 'pages', 'sharingActions'], window);

  if (!actions) return { actions: {} };

  return {
    actions: bindActionCreators({
      addNumber: (ctn) => actions.addNumberPopup.update({ opened: true, profileCtn: ctn }),
      fetchUpsalePopupContent: (data) => actions.upsalePopupContent.update(data),
      openUpsalePopup: () => actions.upsalePopup.update({ opened: true }),
      switchNumber: (ctn, status) => actions.switchNumber(ctn, status),
      disconnectNumber: (ctn, status) => actions.disconnectNumber(ctn, status),
      showFamilyExpansionError: actions.showFamilyExpansionError,
    }, dispatch),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(BundlesSharing);
